<template>
	<div ref="root">
		<slot v-if="shouldRender || forceRender" />
		<div
			v-else
			class="lazy-placeholder"
			:style="{ minHeight: placeholderHeight + 'px' }"
		/>
	</div>
</template>

<script>
export default {
	name: "LazyRender",

	props: {
		placeholderHeight: {
			type: Number,
			default: 48,
		},
		rootMargin: {
			type: String,
			default: "200px 0px",
		},
		unrenderMargin: {
			type: String,
			default: "600px 0px",
		},
		forceRender: {
			type: Boolean,
			default: false,
		},
	},

	emits: ["rendered"],

	data() {
		return {
			shouldRender: false,
		};
	},

	watch: {
		forceRender(val) {
			if (val) {
				this.shouldRender = true;
				this.$nextTick(() => this.$emit("rendered"));
			}
		},
	},

	mounted() {
		this.setupObservers();
	},

	beforeUnmount() {
		this.teardownObservers();
	},

	methods: {
		setupObservers() {
			const scrollParent = this.findScrollParent(this.$el);
			const root =
				scrollParent === document.documentElement ? null : scrollParent;

			// Observer to render: triggers when element is near viewport
			this.renderObserver = new IntersectionObserver(
				([entry]) => {
					if (entry.isIntersecting && !this.shouldRender) {
						this.shouldRender = true;
						this.$nextTick(() => this.$emit("rendered"));
					}
				},
				{ root, rootMargin: this.rootMargin, threshold: 0 },
			);

			// Observer to unrender: uses a larger margin so we only
			// unrender when the element is far from the viewport
			this.unrenderObserver = new IntersectionObserver(
				([entry]) => {
					if (!entry.isIntersecting && this.shouldRender && !this.forceRender) {
						this.shouldRender = false;
					}
				},
				{ root, rootMargin: this.unrenderMargin, threshold: 0 },
			);

			this.renderObserver.observe(this.$refs.root);
			this.unrenderObserver.observe(this.$refs.root);
		},

		teardownObservers() {
			this.renderObserver?.disconnect();
			this.unrenderObserver?.disconnect();
		},

		findScrollParent(el) {
			let node = el?.parentElement;
			while (node) {
				const overflow = getComputedStyle(node).overflowY;
				if (overflow === "auto" || overflow === "scroll") return node;
				node = node.parentElement;
			}
			return document.documentElement;
		},
	},
};
</script>

<style scoped>
.lazy-placeholder {
	background: rgba(0, 0, 0, 0.02);
	border: 1px solid var(--border-default);
	border-radius: 4px;
	margin-bottom: 12px;
}
</style>
